import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { useWireState } from '@forminator/react-wire'
import { MdDisplaySettings } from 'react-icons/md'
import * as store from '@store'
import CustomModal from '../CustomModal/CustomModal'
import SortableList from '../DraggableLists/SortableList'
import '@components/shared/DraggableLists/DraggableLists.css'

/**
 * @typedef HeaderProp
 * @property {string} id
 * @property {string} label
 * @property {string?} field
 * @property {boolean} checked
 */

/**
 * @typedef TableHeaderManagerProps
 * @property {Object} storeName
 * @property {HeaderProp[]} headers
 */

/**
 * 
 * @param {TableHeaderManagerProps} props 
 * @returns 
 */
const TableHeaderManager = forwardRef(({ storeName, headers }, ref) => {
    
    const sortAndCheck = storeHeaders => {
        
        const result = storeHeaders.map(s => {
            const h = headers.find(({ id }) => id === s)
            
            if (!h) return {}
            const label = typeof h.label === 'string' ? h.label : Object.values(h.label).join('/')
            
            return { ...h, label, checked: true }
        }).filter(s => !!s.id)
        
        result.push(
            ...headers
                .filter(({ id }) => !storeHeaders.includes(id))
                .map(h => ({
                    id: h.id,
                    label: typeof h.label === 'string' ? h.label : Object.values(h.label).join('/'),
                    checked: storeHeaders.length > 0 ? false : h.defaultChecked,
                })),
        )
        
        return result
    }
    
    const resetHeaders = () => {
        const mappedHeadersWithLabels = headers.map( k => { 
            
            const fullLabel = typeof k.label === 'object' && k.label.deed && k.label.lease 
                ? `${k.label.deed}/${k.label.lease}`
                : k.label
            
            return {
                id: k.id,
                label: fullLabel,
                checked: k.defaultChecked,
            }
        })
        
        // const idsArray = mappedHeadersWithLabels.filter(h => h.checked).map(k => k.id)
        
        setSelectedHeaders(mappedHeadersWithLabels)
        // setStoreHeaders(idsArray)
    } 
    
    const [showManagerModal, setShowManagerModal] = useState(false)
    const [storeHeaders, setStoreHeaders] = useWireState(store[storeName])
    const [selectedHeaders, setSelectedHeaders] = useState(sortAndCheck(storeHeaders))
    
    useEffect(() => {
        if (!storeHeaders?.length) {
            
            resetHeaders()
        } 
    }, [])
    
    const updateHeaderPositions = () => {
        const checkedHeaders = selectedHeaders.filter(h => h.checked).map(h => h.id)
        
        setStoreHeaders(checkedHeaders)
        setShowManagerModal(false)
    }
    
    const open = () => {
        setSelectedHeaders(sortAndCheck(storeHeaders))
        setShowManagerModal(true)
    }
    
    const close = () => setShowManagerModal(false)
    const reset = resetHeaders
    
    useImperativeHandle(ref, () => ({
        open,
        close,
        reset,
    }))
    
    return (
        <>
            {!ref && <MdDisplaySettings className="text-2xl cursor-pointer" onClick={open} />}
            <CustomModal open={showManagerModal} modalHandler={close}>
                <div className="modal-header">
                    Columns ordering
                </div>
                <div>
                    <div className="h-[calc(70vh-212px)] overflow-y-auto p-3">
                        <SortableList
                            hasCheckbox={true}
                            className=""
                            itemClassName=""
                            id="tableHeaders"
                            items={selectedHeaders}
                            setItems={setSelectedHeaders} />
                    </div>
                </div>
                <div className="modal-footer flex gap-3">
                    <button className="btn btn-outline" onClick={close}>
                        Cancel
                    </button>
                    <button className="btn btn-secondary" onClick={reset}>
                        Reset
                    </button>
                    <button className="btn btn-primary" onClick={updateHeaderPositions}>
                        Save
                    </button>
                </div>
            </CustomModal>
        </>
    )
})

TableHeaderManager.displayName = 'TableHeaderManager'

export default TableHeaderManager
